import { createFileRoute, Link } from "@tanstack/react-router";
import { useContext, useMemo, useState } from "react";
import clsx from "clsx";
import {
  ActionChip,
  AppError,
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  ChipGroup,
  categoricalSlotByIndex,
  categoricalSlotByKey,
  type CategoricalSlot,
  CountBadge,
  DismissibleChip,
  EmptyState,
  Loader,
  Modal,
  Search,
  TextInput,
  toast,
  ToggleChip,
} from "@bfc-software/pallet";
// eslint-disable-next-line no-restricted-imports -- follow-up: use useCurrentLocalization()
import { TFunction } from "i18next";
import { AuthContext, AuthUtils, logger } from "@bfc-software/app-toolbox";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";
import { useIssApi } from "@bfc-software/bfc-web-ui/lib/iss-api-client";
import { apiClient as aaaApiClient } from "@bfc-software/bfc-web-ui/lib/aaa-api-client";
import {
  useOrgCfgApi,
  useOrgCfgFetchClient,
  type OrgCfgFetchClient,
} from "@bfc-software/bfc-web-ui/lib/orgcfg-api-client";

/**
 * Same scope as selectors.tsx's `APP_NAME` -- app users (and now app user
 * groups) are shared across every app on a warehouse, and this is what
 * scopes both to the ones that sign in on the floor.
 */
const APP_NAME = "select";

/**
 * The `group` key of the `selectorSettings` enum (org_config.yaml) --
 * `appUserGroups.users` is this feature's own membership list and the
 * authoritative source of truth, but selectors.tsx's table/detail view still
 * reads a selector's group from this per-user settings key, so every
 * membership change here is mirrored onto it.
 */
const SETTINGS_GROUP_KEY = "group";

/**
 * Mirrors a group-membership change onto the selector's own settings record
 * so selectors.tsx's "User group" column stays truthful. Fetches the
 * selector's current settings first (an update on a selector with no
 * settings record yet 404s, so a missing record falls back to `add` instead)
 * and merges the group key in rather than overwriting the whole map, since
 * the other selectorSettings keys (task type eligibility, validation level,
 * etc.) are unrelated to group membership.
 */
async function syncSelectorGroupSetting(
  client: OrgCfgFetchClient,
  orgId: string,
  warehouseId: string,
  userId: string,
  groupName: string | null,
): Promise<void> {
  const { data, response } = await client.GET("/orgs/user_settings/get", {
    params: { query: { orgId, warehouseId, userId } },
  });
  const exists = response.status !== 404;
  const nextSettings: Record<string, string> = exists
    ? { ...data?.settings }
    : {};
  if (groupName) {
    nextSettings[SETTINGS_GROUP_KEY] = groupName;
  } else {
    delete nextSettings[SETTINGS_GROUP_KEY];
  }

  const { error } = await client.POST(
    exists ? "/orgs/user_settings/update" : "/orgs/user_settings/add",
    { body: { orgId, warehouseId, userId, settings: nextSettings } },
  );
  if (error) throw error;
}

export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/user-groups",
)({
  component: RouteComponent,
});

type Zone = {
  id: number;
  name: string;
};

type AppUser = {
  id: number;
  user_id: string;
  user_name?: string;
  active: boolean;
};

type AppUserGroup = {
  id?: number;
  orgId: string;
  warehouseId: string;
  appName: string;
  name: string;
  zones: string[];
  users: string[];
  customerIds?: number[];
};

type Customer = {
  id: number;
  name: string;
};

/**
 * `customerIds`' sentinel: a single entry of 0 means every customer, including
 * ones synced in later. Otherwise the list is exactly the customers the group
 * covers. The service rejects an empty list and a 0 mixed with other ids, and
 * defaults an omitted list to `[0]` on add.
 */
const ALL_CUSTOMERS_ID = 0;
const ALL_CUSTOMERS: number[] = [ALL_CUSTOMERS_ID];

function coversAllCustomers(customerIds: number[] | undefined): boolean {
  return (
    customerIds === undefined ||
    customerIds.length === 0 ||
    customerIds.includes(ALL_CUSTOMERS_ID)
  );
}

/** The customer ids the group covers, with the `[0]` sentinel expanded to every known customer. */
function expandCustomerIds(
  customerIds: number[] | undefined,
  customers: Customer[],
): number[] {
  return coversAllCustomers(customerIds)
    ? customers.map((customer) => customer.id)
    : (customerIds as number[]);
}

/**
 * `current` after checking or unchecking `targetIds`. Selecting every
 * customer does not collapse back to the `[0]` sentinel -- that is the
 * explicit "automatically include new customers" choice, so an admin who
 * turned it off keeps it off. May return an empty list; the picker holds that
 * as a draft because the service rejects it.
 */
function setCustomersSelected(
  current: number[],
  targetIds: number[],
  isSelected: boolean,
): number[] {
  if (isSelected) {
    const missing = targetIds.filter((id) => !current.includes(id));
    return missing.length === 0 ? current : [...current, ...missing];
  }
  return current.filter((id) => !targetIds.includes(id));
}

type CustomerFilter = "all" | "selected" | "notSelected";

/** AAA's `user_name` is optional -- a selector created before it existed, or without one set, falls back to the login id. */
function displayNameOf(user: AppUser): string {
  return user.user_name || user.user_id;
}

/**
 * openapi-react-query throws the parsed apiResponse body itself
 * (`{ code, message }`) rather than an `Error` -- see the analogous helper
 * in selectors.tsx and zones.tsx.
 */
function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object") {
    const body = error as { message?: unknown; error?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
    if (typeof body.error === "string" && body.error) return body.error;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

function moveItem<T>(items: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (target < 0 || target >= items.length) return items;
  const next = items.slice();
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item!);
  return next;
}

function ZonePriorityBadge({ position }: { position: number }) {
  return (
    <span className="flex flex-none h-16 w-16 items-center justify-center rounded-full bg-on-surface text-surface text-size-11 font-heading">
      {position}
    </span>
  );
}

/**
 * The customers section shared by the create form and the edit panel: a
 * checkbox per customer, searchable, with Select all, an All / Selected / Not
 * selected filter and an "automatically include new customers" option. The
 * list is controlled through `customerIds` using the `[0]` = all convention
 * above.
 *
 * Deselecting every customer is allowed while the admin is working toward a
 * short list, but the service rejects an empty list, so that state lives here
 * as a draft: `onChange` only ever receives a non-empty list, and
 * `onEmptyChange` tells a form to hold its submit until a customer is picked.
 */
function CustomerPicker({
  customers,
  customerIds,
  onChange,
  onEmptyChange,
  t,
}: {
  customers: Customer[];
  customerIds: number[] | undefined;
  onChange: (customerIds: number[]) => void;
  onEmptyChange?: (isEmpty: boolean) => void;
  t: TFunction;
}) {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<CustomerFilter>("all");
  const [isEmptyDraft, setIsEmptyDraft] = useState(false);

  const selectedIds = isEmptyDraft
    ? []
    : expandCustomerIds(customerIds, customers);
  const selected = new Set(selectedIds);
  const includesNew = !isEmptyDraft && coversAllCustomers(customerIds);
  const everySelected =
    customers.length > 0 && selected.size === customers.length;

  const query = search.trim().toLowerCase();
  const visible = customers.filter(
    (customer) =>
      (!query || customer.name.toLowerCase().includes(query)) &&
      (filter === "all" ||
        (filter === "selected") === selected.has(customer.id)),
  );
  const visibleIds = visible.map((customer) => customer.id);
  const allVisibleSelected =
    visible.length > 0 &&
    visible.every((customer) => selected.has(customer.id));

  const commit = (next: number[]) => {
    const isEmpty = next.length === 0;
    setIsEmptyDraft(isEmpty);
    onEmptyChange?.(isEmpty);
    if (!isEmpty) onChange(next);
  };

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-wrap items-center gap-8">
        <span className="section-heading">
          {t("userGroups.customers.heading")}
        </span>
        {includesNew ? (
          <Badge
            label={t("userGroups.customers.allInclNew")}
            color={{ mode: "static", tone: "success" }}
          />
        ) : (
          <Badge
            label={t("userGroups.customers.selectedCount", {
              count: selected.size,
            })}
            color={{ mode: "static", tone: "neutral" }}
          />
        )}
      </div>
      <p className="text-size-12 text-on-surface-variant">
        {t("userGroups.customers.intro")}
      </p>
      <div className="flex flex-wrap items-center justify-between gap-8">
        {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
        <div className="flex-1" style={{ maxWidth: 280 }}>
          <Search
            aria-label={t("userGroups.customers.searchAriaLabel")}
            placeholder={t("userGroups.customers.searchPlaceholder")}
            value={search}
            onChange={(value) => setSearch(String(value))}
            onClear={() => setSearch("")}
          />
        </div>
        <ChipGroup
          ariaLabel={t("userGroups.customers.filterAriaLabel")}
          selected={filter}
          onSelect={setFilter}
          chips={[
            { key: "all", label: t("userGroups.customers.filter.all") },
            {
              key: "selected",
              label: t("userGroups.customers.filter.selected"),
            },
            {
              key: "notSelected",
              label: t("userGroups.customers.filter.notSelected"),
            },
          ]}
        />
      </div>
      <div className="flex items-center justify-between gap-8">
        <Checkbox
          name="select-all-customers"
          value="all"
          label={t("userGroups.customers.selectAll")}
          isSelected={allVisibleSelected}
          isDisabled={visible.length === 0}
          onChange={(isSelected) =>
            commit(setCustomersSelected(selectedIds, visibleIds, isSelected))
          }
        />
        <span className="text-size-12 text-on-surface-variant">
          {t("userGroups.customers.selectedOfTotal", {
            selected: selected.size,
            total: customers.length,
          })}
        </span>
      </div>
      <div
        className="flex flex-wrap gap-x-16 gap-y-8 overflow-y-auto"
        // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
        style={{ maxHeight: 240 }}
      >
        {visible.map((customer) => (
          // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
          <div key={customer.id} style={{ width: 200 }}>
            <Checkbox
              name="customer"
              value={String(customer.id)}
              label={customer.name}
              isSelected={selected.has(customer.id)}
              onChange={(isSelected) =>
                commit(
                  setCustomersSelected(selectedIds, [customer.id], isSelected),
                )
              }
            />
          </div>
        ))}
        {visible.length === 0 ? (
          <p className="text-size-12 text-on-surface-variant">
            {t("userGroups.customers.noMatches")}
          </p>
        ) : null}
      </div>
      <Checkbox
        name="auto-include-new-customers"
        value="auto"
        label={t("userGroups.customers.autoIncludeNew")}
        isSelected={includesNew}
        isDisabled={!everySelected && !includesNew}
        onChange={(isSelected) =>
          commit(
            isSelected
              ? ALL_CUSTOMERS
              : customers.map((customer) => customer.id),
          )
        }
      />
    </div>
  );
}

function ZoneChip({ name, slot }: { name: string; slot: CategoricalSlot }) {
  return <Badge label={name} color={{ mode: "categorical", slot }} />;
}

function SelectorAvatar({ user }: { user: AppUser }) {
  return <Avatar name={displayNameOf(user)} colorKey={user.user_id} />;
}

/**
 * The create-group form -- an inline panel injected under the "Create user
 * group" button rather than a modal, matching zones.tsx's CreateZonePanel
 * (picking zones and selectors stays in the same view as the rest of the
 * group list).
 */
function CreateGroupPanel({
  allZoneNames,
  zoneSlot,
  unassignedSelectors,
  customers,
  onSubmit,
  onClose,
  t,
}: {
  allZoneNames: string[];
  zoneSlot: (name: string) => CategoricalSlot;
  unassignedSelectors: AppUser[];
  customers: Customer[];
  onSubmit: (values: {
    name: string;
    zones: string[];
    users: string[];
    customerIds: number[];
  }) => Promise<void>;
  onClose: () => void;
  t: TFunction;
}) {
  const [name, setName] = useState("");
  const [zones, setZones] = useState<string[]>([]);
  const [userIds, setUserIds] = useState<string[]>([]);
  const [customerIds, setCustomerIds] = useState<number[]>(ALL_CUSTOMERS);
  const [noCustomers, setNoCustomers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setZoneSelected = (zoneName: string, isSelected: boolean) => {
    setZones((prev) =>
      isSelected
        ? prev.includes(zoneName)
          ? prev
          : [...prev, zoneName]
        : prev.filter((z) => z !== zoneName),
    );
  };

  const setSelectorSelected = (userId: string, isSelected: boolean) => {
    setUserIds((prev) =>
      isSelected
        ? prev.includes(userId)
          ? prev
          : [...prev, userId]
        : prev.filter((id) => id !== userId),
    );
  };

  const canSubmit = name.trim().length > 0 && zones.length > 0 && !noCustomers;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting || !canSubmit) return;

    setIsSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        zones,
        users: userIds,
        customerIds,
      });
      onClose();
    } catch {
      // onSubmit already reported the failure via toast; keep the panel open
      // so the admin can retry without re-entering values.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} data-test-role="create-group-panel">
      <Card
        variant="panel"
        layout="body-only"
        body={
          <>
            <div className="panel-title">
              {t("userGroups.form.createTitle")}
            </div>

            <TextInput
              name="name"
              label={t("userGroups.form.nameLabel")}
              placeholder={t("userGroups.form.namePlaceholder")}
              value={name}
              onChange={(ev) => setName(ev.target.value)}
              autofocus={true}
              isRequired={true}
            />

            <div>
              <span className="block mb-8 text-size-13 font-strong text-on-surface-variant">
                {t("userGroups.form.zonesLabel")}
              </span>
              <div className="flex flex-wrap items-center gap-8">
                {allZoneNames.map((zoneName) => {
                  const selected = zones.includes(zoneName);
                  return (
                    <ToggleChip
                      key={zoneName}
                      label={zoneName}
                      isSelected={selected}
                      onChange={(isSelected) =>
                        setZoneSelected(zoneName, isSelected)
                      }
                    />
                  );
                })}
              </div>
            </div>

            {zones.length > 0 ? (
              <div className="flex flex-col gap-8 py-12 px-16 border border-outline rounded-10 bg-surface-container">
                <span className="block mb-8 text-size-13 font-strong text-on-surface-variant">
                  {t("userGroups.form.priorityHeading")}
                </span>
                <p className="text-size-12 text-on-surface-variant">
                  {t("userGroups.form.priorityIntro")}
                </p>
                {zones.map((zoneName, index) => (
                  <div
                    key={zoneName}
                    className="flex items-center justify-between gap-8 p-8 rounded-8 bg-surface"
                  >
                    <div className="flex flex-wrap items-center gap-8">
                      <ZonePriorityBadge position={index + 1} />
                      <ZoneChip name={zoneName} slot={zoneSlot(zoneName)} />
                    </div>
                    <div className="flex items-center gap-4 flex-none">
                      <Button
                        kind="icon-only"
                        variant="tertiary"
                        icon="ChevronUp"
                        label={t("userGroups.rowActions.moveUp")}
                        isDisabled={index === 0}
                        onPress={() =>
                          setZones((prev) => moveItem(prev, index, -1))
                        }
                      />
                      <Button
                        kind="icon-only"
                        variant="tertiary"
                        icon="ChevronDown"
                        label={t("userGroups.rowActions.moveDown")}
                        isDisabled={index === zones.length - 1}
                        onPress={() =>
                          setZones((prev) => moveItem(prev, index, 1))
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : null}

            <CustomerPicker
              customers={customers}
              customerIds={customerIds}
              onChange={setCustomerIds}
              onEmptyChange={setNoCustomers}
              t={t}
            />

            <div>
              <span className="block mb-8 text-size-13 font-strong text-on-surface-variant">
                {t("userGroups.form.selectorsLabel")}
              </span>
              <p className="mt-4 text-size-12 text-on-surface-variant">
                {userIds.length > 0
                  ? t("userGroups.form.selectorsJoining", {
                      count: userIds.length,
                    })
                  : t("userGroups.form.selectorsOptional")}
              </p>
              <div className="flex flex-wrap items-center gap-8">
                {unassignedSelectors.map((user) => {
                  const selected = userIds.includes(user.user_id);
                  return (
                    <ToggleChip
                      key={user.user_id}
                      label={displayNameOf(user)}
                      isSelected={selected}
                      onChange={(isSelected) =>
                        setSelectorSelected(user.user_id, isSelected)
                      }
                      adornment={
                        <Avatar
                          name={displayNameOf(user)}
                          colorKey={user.user_id}
                          size="sm"
                        />
                      }
                    />
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-8">
              <Button
                kind="text-only"
                variant="avatar-surface"
                type="submit"
                text={
                  isSubmitting
                    ? t("userGroups.form.saving")
                    : t("userGroups.form.submit")
                }
                isDisabled={isSubmitting || !canSubmit}
              />
              <Button
                kind="text-only"
                type="button"
                variant="tertiary"
                text={t("userGroups.form.cancel")}
                onPress={onClose}
                isDisabled={isSubmitting}
              />
            </div>
          </>
        }
      />
    </form>
  );
}

/**
 * A group's read-only expansion -- its current selectors, each removable
 * with a single click. Opened via the row's caret.
 */
function GroupSelectorList({
  group,
  selectorById,
  onRemoveSelector,
  t,
}: {
  group: AppUserGroup;
  selectorById: Map<string, AppUser>;
  onRemoveSelector: (userId: string) => void;
  t: TFunction;
}) {
  if (group.users.length === 0) {
    return (
      <p className="px-16 py-12 text-size-13 text-on-surface-variant border-t border-outline">
        {t("userGroups.edit.noSelectors")}
      </p>
    );
  }

  return (
    <div className="flex flex-col border-t border-outline">
      {group.users.map((userId, index) => {
        const user = selectorById.get(userId);
        return (
          <div
            key={userId}
            className={clsx(
              "flex items-center gap-12 px-16 py-12 border-t border-outline-variant",
              index === 0 && "border-t-0",
            )}
          >
            {user ? <SelectorAvatar user={user} /> : null}
            <span className="font-strong text-on-surface">
              {user ? displayNameOf(user) : userId}
            </span>
            <span className="text-on-surface-variant text-size-13">
              {group.zones.join(", ")}
            </span>
            <span className="flex-1" />
            <Button
              kind="text-only"
              variant="tertiary"
              text={t("userGroups.rowActions.removeSelector")}
              onPress={() => onRemoveSelector(userId)}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The full edit surface for an existing group -- direct manipulation of its
 * zone priority order and its selector membership, each change saved
 * immediately (no separate "save" step, matching zones.tsx's product-type
 * editing).
 */
function GroupEditPanel({
  group,
  allZoneNames,
  zoneSlot,
  unassignedSelectors,
  selectorById,
  customers,
  onChangeZones,
  onChangeCustomers,
  onAddSelector,
  onRemoveSelector,
  t,
}: {
  group: AppUserGroup;
  allZoneNames: string[];
  zoneSlot: (name: string) => CategoricalSlot;
  unassignedSelectors: AppUser[];
  selectorById: Map<string, AppUser>;
  customers: Customer[];
  onChangeZones: (zones: string[]) => void;
  onChangeCustomers: (customerIds: number[]) => void;
  onAddSelector: (userId: string) => void;
  onRemoveSelector: (userId: string) => void;
  t: TFunction;
}) {
  const addableZones = allZoneNames.filter((z) => !group.zones.includes(z));

  return (
    // An equal-track grid and a 760px breakpoint both have no utility, so
    // these two columns are a wrapping flex row with an inline min-width.
    // They wrap at their own content width rather than at a fixed breakpoint.
    <div className="flex flex-wrap gap-24 border-t border-outline bg-surface-container p-16">
      {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
      <div className="flex flex-1 flex-col gap-8" style={{ minWidth: 280 }}>
        <span className="section-heading">
          {t("userGroups.edit.zonesHeading")}
        </span>
        <p className="text-size-12 text-on-surface-variant">
          {t("userGroups.edit.zonesIntro")}
        </p>

        <div className="flex flex-col gap-8">
          {group.zones.map((zoneName, index) => (
            <div
              key={zoneName}
              className="flex items-center justify-between gap-8 p-8 rounded-8 bg-surface"
            >
              <div className="flex items-center gap-8 min-w-0">
                <ZonePriorityBadge position={index + 1} />
                <DismissibleChip
                  label={zoneName}
                  color={{ mode: "categorical", slot: zoneSlot(zoneName) }}
                  onDismiss={() =>
                    onChangeZones(group.zones.filter((z) => z !== zoneName))
                  }
                  dismissLabel={t("userGroups.rowActions.removeZone")}
                />
              </div>
              <div className="flex items-center gap-4 flex-none">
                <Button
                  kind="icon-only"
                  variant="tertiary"
                  icon="ChevronUp"
                  label={t("userGroups.rowActions.moveUp")}
                  isDisabled={index === 0}
                  onPress={() =>
                    onChangeZones(moveItem(group.zones, index, -1))
                  }
                />
                <Button
                  kind="icon-only"
                  variant="tertiary"
                  icon="ChevronDown"
                  label={t("userGroups.rowActions.moveDown")}
                  isDisabled={index === group.zones.length - 1}
                  onPress={() => onChangeZones(moveItem(group.zones, index, 1))}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-8">
          <span className="text-size-12 font-strong text-on-surface-variant">
            {t("userGroups.edit.addZone")}
          </span>
          <div className="flex flex-wrap gap-8">
            {addableZones.map((zoneName) => (
              <ActionChip
                key={zoneName}
                label={zoneName}
                onPress={() => onChangeZones([...group.zones, zoneName])}
              />
            ))}
            {addableZones.length === 0 ? (
              <p className="text-size-12 text-on-surface-variant">
                {t("userGroups.edit.noZonesLeft")}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
      <div className="flex flex-1 flex-col gap-8" style={{ minWidth: 280 }}>
        <span className="section-heading">
          {t("userGroups.edit.selectorsHeading")}
        </span>
        <p className="text-size-12 text-on-surface-variant">
          {t("userGroups.selectorsAssigned", { count: group.users.length })}
        </p>

        <div className="flex flex-col gap-8">
          {group.users.map((userId) => {
            const user = selectorById.get(userId);
            return (
              <div
                key={userId}
                className="flex items-center justify-between gap-8 p-8 rounded-8 bg-surface"
              >
                <div className="flex items-center gap-8 min-w-0">
                  {user ? <SelectorAvatar user={user} /> : null}
                  <DismissibleChip
                    label={user ? displayNameOf(user) : userId}
                    color={{ mode: "static", tone: "neutral" }}
                    onDismiss={() => onRemoveSelector(userId)}
                    dismissLabel={t("userGroups.rowActions.removeSelector")}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col gap-8">
          <span className="text-size-12 font-strong text-on-surface-variant">
            {t("userGroups.edit.addSelector")}
          </span>
          <p className="mt-4 text-size-12 text-on-surface-variant">
            {t("userGroups.edit.addSelectorIntro")}
          </p>
          <div className="flex flex-wrap gap-8">
            {unassignedSelectors.map((user) => (
              <ActionChip
                key={user.user_id}
                label={displayNameOf(user)}
                onPress={() => onAddSelector(user.user_id)}
              />
            ))}
            {unassignedSelectors.length === 0 ? (
              <p className="text-size-12 text-on-surface-variant">
                {t("userGroups.edit.noSelectorsLeft")}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-8 w-full p-16 border border-outline rounded-10 bg-surface">
        <CustomerPicker
          customers={customers}
          customerIds={group.customerIds}
          onChange={onChangeCustomers}
          t={t}
        />
      </div>
    </div>
  );
}

function GroupRow({
  group,
  allZoneNames,
  zoneSlot,
  selectorById,
  unassignedSelectors,
  customers,
  isOpen,
  mode,
  onToggleView,
  onToggleEdit,
  onChangeZones,
  onChangeCustomers,
  onAddSelector,
  onRemoveSelector,
  onDelete,
  t,
}: {
  group: AppUserGroup;
  allZoneNames: string[];
  zoneSlot: (name: string) => CategoricalSlot;
  selectorById: Map<string, AppUser>;
  unassignedSelectors: AppUser[];
  customers: Customer[];
  isOpen: boolean;
  mode: "view" | "edit";
  onToggleView: () => void;
  onToggleEdit: () => void;
  onChangeZones: (zones: string[]) => void;
  onChangeCustomers: (customerIds: number[]) => void;
  onAddSelector: (userId: string) => void;
  onRemoveSelector: (userId: string) => void;
  onDelete: () => void;
  t: TFunction;
}) {
  return (
    <Card
      layout="body-only"
      data-test-role="user-group-row"
      body={
        <>
          <div className="flex items-center gap-20 py-16 px-16">
            <Button
              kind="icon-only"
              variant="bare"
              icon={isOpen ? "ChevronDown" : "ChevronRight"}
              label={t(
                isOpen
                  ? "userGroups.rowActions.collapse"
                  : "userGroups.rowActions.expand",
              )}
              aria-expanded={isOpen}
              onPress={onToggleView}
            />

            <div
              className="flex flex-none flex-col gap-4"
              // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
              style={{ minWidth: 160 }}
            >
              <span
                className="text-on-surface text-size-16"
                data-test-role="user-group-name"
              >
                {group.name}
              </span>
              <span className="text-size-13 text-on-surface-variant">
                {t("userGroups.selectorsAssigned", {
                  count: group.users.length,
                })}
              </span>
            </div>

            <div
              className="flex flex-1 flex-col gap-4"
              // eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
              style={{ minWidth: 200 }}
            >
              <span className="section-heading">
                {t("userGroups.zonesByPriority")}
              </span>
              <div className="flex flex-wrap items-center gap-8">
                {group.zones.map((zoneName, index) => (
                  <span
                    key={zoneName}
                    className="flex flex-wrap items-center gap-8"
                  >
                    <ZonePriorityBadge position={index + 1} />
                    <ZoneChip name={zoneName} slot={zoneSlot(zoneName)} />
                  </span>
                ))}
              </div>
            </div>

            <div className="flex flex-none flex-col gap-4">
              <span className="section-heading">
                {t("userGroups.customers.heading")}
              </span>
              <div className="flex flex-wrap items-center gap-4">
                {coversAllCustomers(group.customerIds) ? (
                  <Badge
                    label={t("userGroups.customers.all")}
                    color={{ mode: "static", tone: "success" }}
                  />
                ) : (
                  <Badge
                    label={t("userGroups.customers.selectedCount", {
                      count: group.customerIds?.length ?? 0,
                    })}
                    color={{ mode: "static", tone: "neutral" }}
                  />
                )}
              </div>
            </div>

            <div className="flex items-center gap-8 flex-none ml-auto">
              <Button
                kind="icon-only"
                variant={isOpen && mode === "edit" ? "filled" : "tertiary"}
                icon={isOpen && mode === "edit" ? "Check" : "Edit2"}
                label={t(
                  isOpen && mode === "edit"
                    ? "userGroups.rowActions.doneEditing"
                    : "userGroups.rowActions.edit",
                )}
                onPress={onToggleEdit}
                data-test-role="user-group-edit"
              />
              <Button
                kind="text-only"
                variant="tertiary"
                text={t("userGroups.rowActions.delete")}
                isDisabled={group.users.length > 0}
                onPress={onDelete}
              />
            </div>
          </div>

          {isOpen && mode === "view" ? (
            <GroupSelectorList
              group={group}
              selectorById={selectorById}
              onRemoveSelector={onRemoveSelector}
              t={t}
            />
          ) : null}

          {isOpen && mode === "edit" ? (
            <GroupEditPanel
              group={group}
              allZoneNames={allZoneNames}
              zoneSlot={zoneSlot}
              unassignedSelectors={unassignedSelectors}
              selectorById={selectorById}
              customers={customers}
              onChangeZones={onChangeZones}
              onChangeCustomers={onChangeCustomers}
              onAddSelector={onAddSelector}
              onRemoveSelector={onRemoveSelector}
              t={t}
            />
          ) : null}
        </>
      }
    />
  );
}

function DeleteGroupConfirm({
  group,
  isPending,
  onCancel,
  onConfirm,
  t,
}: {
  group: AppUserGroup | undefined;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  t: TFunction;
}) {
  return (
    <Modal
      control="external"
      data-test-role="delete-group-confirm"
      isDismissable={!isPending}
      isOpen={group !== undefined}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      header={<h2>{t("userGroups.deleteConfirm.title")}</h2>}
      footer={
        <>
          <Button
            kind="text-only"
            text={t("userGroups.deleteConfirm.cancel")}
            variant="tertiary"
            onPress={onCancel}
            isDisabled={isPending}
          />
          <Button
            kind="text-only"
            text={t("userGroups.deleteConfirm.confirm")}
            variant="danger"
            onPress={onConfirm}
            isDisabled={isPending}
          />
        </>
      }
    >
      <div className="delete-confirm">
        <p className="delete-confirm-body">
          {t("userGroups.deleteConfirm.body")}
        </p>
        {group ? <p className="delete-confirm-target">{group.name}</p> : null}
      </div>
    </Modal>
  );
}

export function RouteComponent() {
  const params = Route.useParams();
  const { state: authState } = useContext(AuthContext);
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const issClient = useIssApi();
  const aaaClient = useMemo(() => aaaApiClient(), []);
  const orgCfgClient = useOrgCfgApi();
  const orgCfgFetchClient = useOrgCfgFetchClient();

  // eslint-disable-next-line no-restricted-properties -- TODO: migrate to useAssertedTenancyContext()
  const orgId = AuthUtils.getOrgIdByStub(authState, params.org) ?? params.org;
  // eslint-disable-next-line no-restricted-properties -- TODO: migrate to useAssertedTenancyContext()
  const warehouseId = AuthUtils.getWarehouseId(
    authState,
    orgId,
    params.location,
  );
  const idsReady = typeof warehouseId === "string";

  const zonesQuery = issClient.useQuery("get", "/zones/all", {});
  const selectorsQuery = aaaClient.useQuery(
    "get",
    "/app_user/list",
    {
      params: {
        query: {
          org_id: orgId,
          warehouse_id: warehouseId ?? "",
          app_name: APP_NAME,
        },
      },
    },
    { enabled: idsReady },
  );
  // ISS clamps `limit` to 500, far past how many customers a warehouse has.
  // The picker needs them all at once to offer the `[0]` = all toggling, so
  // this is not paged.
  const customersQuery = issClient.useQuery("get", "/customer-recs/list", {
    params: { query: { limit: 500 } },
  });
  const groupsQuery = orgCfgClient.useQuery(
    "get",
    "/orgs/user_groups/list",
    {
      params: {
        query: {
          orgId,
          warehouseId: warehouseId ?? "",
          appName: APP_NAME,
        },
      },
    },
    { enabled: idsReady },
  );

  const addMutation = orgCfgClient.useMutation("post", "/orgs/user_groups/add");
  const updateMutation = orgCfgClient.useMutation(
    "post",
    "/orgs/user_groups/update",
  );
  const removeMutation = orgCfgClient.useMutation(
    "post",
    "/orgs/user_groups/remove",
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [openName, setOpenName] = useState<string | undefined>(undefined);
  const [openMode, setOpenMode] = useState<"view" | "edit">("view");
  const [deleteTarget, setDeleteTarget] = useState<AppUserGroup | undefined>(
    undefined,
  );

  const zones = (zonesQuery.data?.data ?? []) as Zone[];
  const allZoneNames = [...zones]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((zone) => zone.name);
  // Cycled by the zone's own id (not its name) so a renamed zone keeps its
  // color, and so a zone's chip color matches the Zones page's own card
  // border for the same zone. A stale name whose zone is gone falls back to
  // slot 1 rather than an ad hoc grey.
  const zoneSlotByName = new Map<string, CategoricalSlot>(
    zones.map((zone) => [zone.name, categoricalSlotByIndex(zone.id)]),
  );
  const zoneSlot = (name: string) =>
    categoricalSlotByKey(name, zoneSlotByName, 1);

  const customers = [...(customersQuery.data?.data ?? [])]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ id, name }): Customer => ({ id, name }));

  const selectors = (selectorsQuery.data ?? []) as AppUser[];
  const selectorById = new Map(selectors.map((user) => [user.user_id, user]));

  const groups = ((groupsQuery.data ?? []) as AppUserGroup[])
    .slice()
    .sort((a, b) => (a.id ?? 0) - (b.id ?? 0));

  const assignedUserIds = new Set(groups.flatMap((group) => group.users));
  const unassignedSelectors = selectors.filter(
    (user) => !assignedUserIds.has(user.user_id),
  );

  const isLoading =
    !idsReady ||
    zonesQuery.isLoading ||
    selectorsQuery.isLoading ||
    customersQuery.isLoading ||
    groupsQuery.isLoading;
  const isError =
    zonesQuery.isError ||
    selectorsQuery.isError ||
    customersQuery.isError ||
    groupsQuery.isError;

  const refetchGroups = () => {
    groupsQuery.refetch();
  };

  const closeOpen = () => setOpenName(undefined);

  const toggleView = (name: string) => {
    if (openName === name && openMode === "view") {
      closeOpen();
    } else {
      setOpenName(name);
      setOpenMode("view");
    }
  };

  const toggleEdit = (name: string) => {
    if (openName === name && openMode === "edit") {
      closeOpen();
    } else {
      setOpenName(name);
      setOpenMode("edit");
    }
  };

  /**
   * Best-effort: the group's own `users` array is already the membership
   * source of truth by this point, so a failure here (an admin can still fix
   * it from the Selectors page) is warned rather than rolled back.
   */
  const syncSelectorGroup = async (
    userId: string,
    groupName: string | null,
  ) => {
    try {
      await syncSelectorGroupSetting(
        orgCfgFetchClient,
        orgId,
        warehouseId!,
        userId,
        groupName,
      );
    } catch (error) {
      logger.warn("Failed to sync selector's group setting", {
        error,
        userId,
        groupName,
      });
      toast.error(t("userGroups.edit.settingsSyncFailed", { userId }));
    }
  };

  const handleCreateSubmit = async (values: {
    name: string;
    zones: string[];
    users: string[];
    customerIds: number[];
  }) => {
    try {
      await addMutation.mutateAsync({
        body: {
          orgId,
          warehouseId: warehouseId!,
          appName: APP_NAME,
          name: values.name,
          zones: values.zones,
          users: values.users,
          customerIds: values.customerIds,
        },
      });
      logger.info("Created user group", { name: values.name });
      toast.success(t("userGroups.form.createSuccessToast"));
    } catch (error) {
      logger.warn("Failed to create user group", { error, name: values.name });
      toast.error(errorMessage(error, t("userGroups.form.saveFailedFallback")));
      throw error;
    }

    for (const userId of values.users) {
      await syncSelectorGroup(userId, values.name);
    }
    refetchGroups();
  };

  const saveGroup = async (
    group: AppUserGroup,
    patch: { zones?: string[]; users?: string[]; customerIds?: number[] },
  ) => {
    try {
      await updateMutation.mutateAsync({
        body: {
          orgId: group.orgId,
          warehouseId: group.warehouseId,
          appName: group.appName,
          name: group.name,
          zones: patch.zones ?? group.zones,
          users: patch.users ?? group.users,
          // Omitted leaves the stored value alone, so only send a change.
          ...(patch.customerIds ? { customerIds: patch.customerIds } : {}),
        },
      });
      logger.info("Updated user group", { name: group.name });
      refetchGroups();
    } catch (error) {
      logger.warn("Failed to update user group", { error, name: group.name });
      toast.error(
        errorMessage(error, t("userGroups.edit.updateFailedFallback")),
      );
      throw error;
    }
  };

  /** Adds a selector to the group, then mirrors the membership onto their own settings (see `syncSelectorGroupSetting`). */
  const addSelectorToGroup = async (group: AppUserGroup, userId: string) => {
    try {
      await saveGroup(group, { users: [...group.users, userId] });
    } catch {
      return; // saveGroup already reported the failure.
    }
    await syncSelectorGroup(userId, group.name);
  };

  /** Removes a selector from the group, then clears the group off their own settings so they don't read stale. */
  const removeSelectorFromGroup = async (
    group: AppUserGroup,
    userId: string,
  ) => {
    try {
      await saveGroup(group, {
        users: group.users.filter((id) => id !== userId),
      });
    } catch {
      return;
    }
    await syncSelectorGroup(userId, null);
  };

  const closeDeleteConfirm = () => {
    if (removeMutation.isPending) return;
    setDeleteTarget(undefined);
  };

  const confirmDelete = () => {
    if (!deleteTarget || removeMutation.isPending) return;

    const target = deleteTarget;
    removeMutation
      .mutateAsync({
        body: {
          orgId: target.orgId,
          warehouseId: target.warehouseId,
          appName: target.appName,
          name: target.name,
        },
      })
      .then(() => {
        logger.info("Deleted user group", { name: target.name });
        toast.success(t("userGroups.deleteConfirm.successToast"));
        setDeleteTarget(undefined);
        if (openName === target.name) closeOpen();
        refetchGroups();
      })
      .catch((error: unknown) => {
        logger.warn("Failed to delete user group", {
          error,
          name: target.name,
        });
        toast.error(
          errorMessage(error, t("userGroups.deleteConfirm.errorToast")),
        );
      });
  };

  const linkParams = {
    lang: params.lang,
    org: params.org,
    location: params.location,
  };

  return (
    <div className="bg-surface h-full w-full">
      <div className="page-shell">
        <header className="page-header">
          <div className="page-header-left">
            <h1 className="page-title">{t("userGroups.heading")}</h1>
            <p className="page-subtitle">{t("userGroups.subheading")}</p>
          </div>
        </header>

        {isLoading ? (
          <Loader type="page" />
        ) : isError ? (
          <AppError
            description={t("userGroups.failed")}
            resolution={t("userGroups.failureSupport")}
          />
        ) : (
          <>
            {/*
             * `Card`'s body slot is a plain block and does not stack its
             * children, so these bare `<span>`s would run together on one
             * inline line and overlap each other. `Card` has no vocabulary for
             * stacking arbitrary body content, so the flex column goes on an
             * element inside the body rather than at `Card`'s own chrome.
             */}
            <div className="flex flex-wrap gap-16">
              {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
              <div className="flex-1" style={{ minWidth: 200 }}>
                <Card
                  variant="stat"
                  layout="body-only"
                  body={
                    <div className="flex flex-col gap-4">
                      <span className="section-heading">
                        {t("userGroups.stats.groupsDefined")}
                      </span>
                      <span className="text-size-28 text-on-surface">
                        {groups.length}
                      </span>
                    </div>
                  }
                />
              </div>
              {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
              <div className="flex-1" style={{ minWidth: 200 }}>
                <Card
                  variant="stat"
                  layout="body-only"
                  body={
                    <div className="flex flex-col gap-4">
                      <span className="section-heading">
                        {t("userGroups.stats.selectorsInGroup")}
                      </span>
                      <span className="text-size-28 text-on-surface">
                        {assignedUserIds.size}
                      </span>
                    </div>
                  }
                />
              </div>
              {/* eslint-disable-next-line react/forbid-dom-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities */}
              <div className="flex-1" style={{ minWidth: 200 }}>
                {/* `Card` has no categorical/semantic color prop, so the
                    warning accent is an inline `style` on `Card` itself. */}
                <Card
                  variant="stat"
                  layout="body-only"
                  // eslint-disable-next-line react/forbid-component-props -- follow-up: replace hardcoded inline style with flexbox/pallet utilities
                  style={{
                    borderLeft: "4px solid var(--pallet-role-warning)",
                  }}
                  body={
                    <div className="flex flex-col gap-4">
                      <span className="section-heading">
                        {t("userGroups.stats.notYetAssigned")}
                      </span>
                      <span className="text-size-28 text-warning">
                        {unassignedSelectors.length}
                      </span>
                      <span className="text-size-13 text-on-surface-variant">
                        {t("userGroups.stats.notYetAssignedNote")} ·{" "}
                        <Link
                          className="font-strong text-primary no-underline hover:underline"
                          to="/$lang/orgs/$org/outbound/whs/$location/selectors"
                          params={linkParams}
                        >
                          {t("userGroups.stats.viewSelectors")} →
                        </Link>
                      </span>
                    </div>
                  }
                />
              </div>
            </div>

            <div className="flex items-center justify-between gap-16 flex-wrap">
              <div className="flex items-center gap-8">
                <h2 className="text-size-18 text-on-surface">
                  {t("userGroups.listHeading")}
                </h2>
                <CountBadge count={groups.length} />
              </div>
              <Button
                kind="text-icon-left"
                variant="avatar-surface"
                icon="Plus"
                text={t("userGroups.createButton")}
                onPress={() => setCreateOpen(true)}
                isDisabled={openMode === "edit" && openName !== undefined}
              />
            </div>

            {createOpen ? (
              <CreateGroupPanel
                allZoneNames={allZoneNames}
                zoneSlot={zoneSlot}
                unassignedSelectors={unassignedSelectors}
                customers={customers}
                onSubmit={handleCreateSubmit}
                onClose={() => setCreateOpen(false)}
                t={t}
              />
            ) : null}

            {groups.length === 0 ? (
              <EmptyState heading={t("userGroups.empty")} />
            ) : (
              <div className="flex flex-col gap-12">
                {groups.map((group) => (
                  <GroupRow
                    key={group.name}
                    group={group}
                    allZoneNames={allZoneNames}
                    zoneSlot={zoneSlot}
                    selectorById={selectorById}
                    unassignedSelectors={unassignedSelectors}
                    customers={customers}
                    isOpen={openName === group.name}
                    mode={openMode}
                    onToggleView={() => toggleView(group.name)}
                    onToggleEdit={() => toggleEdit(group.name)}
                    onChangeZones={(nextZones) =>
                      saveGroup(group, { zones: nextZones })
                    }
                    onChangeCustomers={(customerIds) =>
                      saveGroup(group, { customerIds })
                    }
                    onAddSelector={(userId) =>
                      addSelectorToGroup(group, userId)
                    }
                    onRemoveSelector={(userId) =>
                      removeSelectorFromGroup(group, userId)
                    }
                    onDelete={() => setDeleteTarget(group)}
                    t={t}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <DeleteGroupConfirm
        group={deleteTarget}
        isPending={removeMutation.isPending}
        onCancel={closeDeleteConfirm}
        onConfirm={confirmDelete}
        t={t}
      />
    </div>
  );
}
